import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Crown, Medal, User } from "lucide-react";
import { apiRankList } from "@/lib/ranking-api";
import { useAuth } from "@/providers/auth-provider";
import { useChats } from "@/providers/chat-provider";
import { InfiniteScroll } from "@/components/blocks/infinite-scroll";

interface RankItem {
  username: string;
  score: number;
  rank: number;
}

interface MyRank {
  rank: number;
  score: number;
}

interface RankData {
  seasonId: string;
  page: number;
  perPage: number;
  topRank: RankItem[];
  friendRank: RankItem[];
  myRankInTop: MyRank | null;
  myRankInFriend: MyRank | null;
}

interface PlayRankingProps {
  gameId: string;
}

const MAX_RANK = 100;
const MAX_PAGE = 10;
const PAGE_COUNT = MAX_RANK / MAX_PAGE;

/** 헬퍼 함수: 현재 월 ID 계산 (KST 기준) **/
function getCurrentMonthId() {
  const now = new Date();
  const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  const year = kst.getUTCFullYear();
  const month = String(kst.getUTCMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

export const Leaderboard = ({ gameId }: PlayRankingProps) => {
  const { user } = useAuth();
  const { chats } = useChats();
  const [open, setOpen] = useState(false);
  const [seasonTab, setSeasonTab] = useState<"all" | "monthly">("all");
  const [rankTypeTab, setRankTypeTab] = useState<"total" | "friend">("total");
  const [topRankList, setTopRankList] = useState<RankItem[]>([]); // 누적 전체 랭킹
  const [friendRankList, setFriendRankList] = useState<RankItem[]>([]); // 친구 랭킹
  const [myRankInTop, setMyRankInTop] = useState<MyRank | null>(null);
  const [myRankInFriend, setMyRankInFriend] = useState<MyRank | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true); // 더 불러올 데이터 여부
  const [loading, setLoading] = useState(false);

  const friendIds = useMemo(() => {
    return chats?.friends?.map((chat: any) => chat.friend._id) || [];
  }, [chats.friends]);

  const myRank = rankTypeTab === "total" ? myRankInTop : myRankInFriend;

  const fetchRankData = useCallback(
    async (pageNum: number = 1, append: boolean = false) => {
      if (!user?._id || !user?.token || !gameId) return;
      setLoading(true);
      try {
        const monthId = getCurrentMonthId();
        const seasonId = seasonTab === "all" ? "all" : monthId;
        const result: RankData = await apiRankList(
          {
            gameId,
            userId: user._id,
            seasonId,
            page: Math.max(1, Math.min(MAX_PAGE, pageNum)),
            perPage: PAGE_COUNT,
            friendIds,
          },
          user.token,
        );

        // topRank 누적 처리
        if (append) {
          setTopRankList((prev) => [...prev, ...result.topRank]);
        } else {
          setTopRankList(result.topRank);
        }

        if (pageNum === 1) {
          setMyRankInTop(result.myRankInTop);
          setMyRankInFriend(result.myRankInFriend);
          setFriendRankList(result.friendRank);
        }

        // hasMore 조건: 페이지 제한 + 받아온 데이터가 PAGE_COUNT와 같은지
        setHasMore(pageNum < MAX_PAGE && result.topRank.length === PAGE_COUNT);
      } catch (error) {
        console.error("Failed to fetch rank data:", error);
      } finally {
        setLoading(false);
      }
    },
    [user, gameId, seasonTab, friendIds],
  );

  const loadMore = useCallback(async () => {
    const nextPage = page + 1;
    setPage(nextPage);
    await fetchRankData(nextPage, true);
  }, [page, fetchRankData]);

  useEffect(() => {
    if (open) {
      // 탭 변경시 초기화
      setPage(1);
      setTopRankList([]);
      setHasMore(true);
      fetchRankData(1, false);
    }
  }, [open, seasonTab, gameId, fetchRankData]);

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return (
      <span className="w-5 h-5 text-center text-sm font-bold">{rank}</span>
    );
  };

  const formatScore = (score: number) => {
    return score.toLocaleString();
  };

  const renderRankList = (items: RankItem[]) => {
    if (items.length === 0) {
      return (
        <div className="flex items-center justify-center h-32 text-muted-foreground">
          랭킹 데이터가 없습니다.
        </div>
      );
    }

    return items.map((item) => (
      <div
        key={`${item.rank}-${item.username}`}
        className="flex items-center justify-between p-3 border-b last:border-b-0 hover:bg-muted/50"
      >
        <div className="flex items-center gap-3">
          {getRankIcon(item.rank)}
          <span className="font-medium">{item.username}</span>
        </div>
        <Badge variant="secondary">{formatScore(item.score)}점</Badge>
      </div>
    ));
  };

  const renderRankItem = (_index: number, item: RankItem) => (
    <div
      key={`${item.rank}-${item.username}`}
      className="flex items-center justify-between p-3 border-b last:border-b-0 hover:bg-muted/50"
    >
      <div className="flex items-center gap-3">
        {getRankIcon(item.rank)}
        <span className="font-medium">{item.username}</span>
      </div>
      <Badge variant="secondary">{formatScore(item.score)}점</Badge>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon">
          <Crown className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="w-5 h-5" />
            랭킹
          </DialogTitle>
          <DialogDescription>
            게임의 랭킹을 확인할 수 있습니다.
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={seasonTab}
          onValueChange={(v) => setSeasonTab(v as "all" | "monthly")}
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="all">전체기간</TabsTrigger>
            <TabsTrigger value="monthly">월간</TabsTrigger>
          </TabsList>
        </Tabs>

        <Tabs
          value={rankTypeTab}
          onValueChange={(v) => setRankTypeTab(v as "total" | "friend")}
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="total">전체랭킹</TabsTrigger>
            <TabsTrigger value="friend">친구랭킹</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex items-center justify-between p-3 bg-primary/10 rounded-lg border border-primary/20">
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            <span className="font-semibold">나의 랭킹</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge>{myRank?.rank}위</Badge>
            <Badge variant="secondary">
              {formatScore(myRank?.score || 0)}점
            </Badge>
          </div>
        </div>

        {rankTypeTab === "total" ? (
          <div className="h-[300px] border rounded-lg overflow-hidden">
            {topRankList.length === 0 && !loading ? (
              <div className="flex items-center justify-center h-32 text-muted-foreground">
                랭킹 데이터가 없습니다.
              </div>
            ) : (
              <InfiniteScroll
                items={topRankList}
                itemRender={renderRankItem}
                loadMore={loadMore}
                hasMore={hasMore}
                isLoading={loading}
                reverseOrder={false}
              />
            )}
          </div>
        ) : (
          <ScrollArea className="h-[300px] border rounded-lg">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                로딩 중...
              </div>
            ) : (
              renderRankList(friendRankList)
            )}
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};
